import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getAuthToken, setAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(Boolean(getAuthToken()));

    const refreshUser = useCallback(async () => {
        const token = getAuthToken();
        if (!token) {
            setUser(null);
            setLoading(false);
            return null;
        }
        try {
            const response = await api.authMe();
            setUser(response.user || null);
            return response.user || null;
        } catch {
            setAuthToken(null);
            setUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    const applySession = useCallback((token, nextUser = null) => {
        setAuthToken(token);
        if (nextUser) setUser(nextUser);
        else refreshUser();
    }, [refreshUser]);

    const logout = useCallback(async () => {
        try {
            await api.authLogout();
        } catch {
            // Local logout should still succeed if the network request fails.
        }
        setAuthToken(null);
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({
            user,
            loading,
            isAuthenticated: Boolean(user),
            applySession,
            refreshUser,
            logout,
        }),
        [applySession, loading, logout, refreshUser, user]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const value = useContext(AuthContext);
    if (!value) throw new Error("useAuth must be used inside AuthProvider");
    return value;
}
